import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import type { RecognitionAppreciationCard } from '../../types/ui-blocks';
import { useBizyChatActions } from '../context/actions-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { AmountPill, HashtagToken, RecipientHandle } from '../lib/recognition-segments';
import { UiBlockActionBar } from '../lib/ui-block-action-bar';
import type { BizyNodeProps } from './node-props';

/**
 * Pollinator mode card. Shows a snapshot of a real recognition post or
 * celebration the user hasn't engaged with yet, then offers one-tap
 * appreciation via backend-supplied actions.
 */
export function RecognitionAppreciationCardNode({ node }: BizyNodeProps) {
	const block = node as RecognitionAppreciationCard;
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { fontSize = 'sm', isReadOnly } = useBizyChatActions();
	const { kind, giver, recipient, amount, message, hashtags, date, actions } = block.payload;
	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));
	const hasHeader = !!giver || recipients.length > 0 || (amount != null && amount > 0);

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{kind === 'celebration' && (
				<Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.softer" mb={1}>
					{t('uiBlocks.recognitionAppreciation.celebrationLabel')}
				</Text>
			)}
			{hasHeader && (
				<Text fontSize={fontSize} lineHeight="tall">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{amount != null && amount > 0 && <AmountPill amount={amount} />}
				</Text>
			)}
			<Text fontSize={fontSize} mt={hasHeader ? 1 : 0} whiteSpace="pre-wrap">
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			{date && (
				<Flex mt={2}>
					<Text fontSize="xs" color="text.softer">
						{date}
					</Text>
				</Flex>
			)}
			<UiBlockActionBar actions={actions} areActionsDisabled={isReadOnly} />
		</Box>
	);
}
